import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Alert, Spinner, Badge, Table, Form, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar, faExclamationTriangle, faCheckCircle,
  faSearch, faCog, faDownload, faEye, faRocket, faPlus,
  faTv, faBroadcastTower, faGlobe, faChartPie
} from '@fortawesome/free-solid-svg-icons';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  ArcElement, Tooltip, Legend, Title
} from 'chart.js';
import infomediaService from '../../services/infomediaService';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, Title);

const MONTH_NAMES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];

const MEDIO_ICONS = {
  Radio: faBroadcastTower, TV: faTv, Internet: faGlobe,
  TVPagada: faTv, Prensa: faChartBar
};

const CHART_COLORS = [
  '#4361ee','#7209b7','#f72585','#4cc9f0','#3a0ca3',
  '#560bad','#480ca8','#3f37c9','#4895ef','#4cc9f0'
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 4 }, (_, i) => currentYear - i);

export default function InfomediaDashboard() {
  const [activeTab, setActiveTab] = useState('competencia');
  const [selectedPeriod, setSelectedPeriod] = useState({
    ano: currentYear,
    mes: new Date().getMonth() + 1
  });

  const [loading, setLoading]   = useState(true);
  const [loadingOpt, setLoadingOpt] = useState(false);
  const [error, setError]       = useState(null);
  const [statistics, setStatistics]       = useState(null);
  const [summaryReport, setSummaryReport] = useState(null);
  const [unmappedSummary, setUnmappedSummary] = useState(null);
  const [currentPeriodData, setCurrentPeriodData] = useState(null);
  const [unmappedStatus, setUnmappedStatus] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [stats, unmapped, report] = await Promise.all([
        infomediaService.getStatistics(selectedPeriod.ano, selectedPeriod.mes),
        infomediaService.getUnmappedRecords(selectedPeriod.ano, selectedPeriod.mes, { limit: 10, prioridad: 'ALTA' }),
        infomediaService.getSummaryReport(selectedPeriod.ano, selectedPeriod.mes),
      ]);
      setStatistics(stats);
      setUnmappedSummary(unmapped);
      setSummaryReport(report);
    } catch (e) {
      setError('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  const loadOptimized = useCallback(async () => {
    setLoadingOpt(true);
    try {
      const [check, period] = await Promise.all([
        infomediaService.checkUnmapped(selectedPeriod.ano, selectedPeriod.mes),
        infomediaService.getCurrentPeriod(selectedPeriod.ano, selectedPeriod.mes, {
          includeMapping: true, includeUnmapped: true, limit: 15
        }),
      ]);
      setUnmappedStatus(check);
      setCurrentPeriodData(period);
    } catch (e) {
      console.error('Error cargando optimizado:', e);
    } finally {
      setLoadingOpt(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    loadDashboard();
    loadOptimized();
  }, [loadDashboard, loadOptimized]);

  const handleDetect = async () => {
    if (!window.confirm(`¿Ejecutar detección para ${MONTH_NAMES[selectedPeriod.mes - 1]} ${selectedPeriod.ano}? Puede tomar varios minutos.`)) return;
    setLoading(true);
    try {
      await infomediaService.detectUnmapped(selectedPeriod.ano, selectedPeriod.mes);
      await Promise.all([loadDashboard(), loadOptimized()]);
    } catch (e) {
      setError('Error en detección: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Gráficas ── */
  const segmentos = summaryReport?.segmentos || [];
  const segmentosConData = segmentos.filter(s => s.segmento !== 'SIN_SEGMENTO' && s.totalSpots > 0);
  const sinSegmento      = segmentos.filter(s => s.segmento === 'SIN_SEGMENTO');
  const displaySegmentos = segmentosConData.length > 0 ? segmentosConData : segmentos;

  const barData = {
    labels: displaySegmentos.slice(0, 10).map(s => s.segmento),
    datasets: [{
      label: 'Spots',
      data: displaySegmentos.slice(0, 10).map(s => s.totalSpots),
      backgroundColor: CHART_COLORS,
      borderRadius: 6,
    }]
  };

  const barOpts = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: true, text: 'Spots por Segmento', font: { size: 14 } }
    },
    scales: { y: { beginAtZero: true } }
  };

  const doughnutData = {
    labels: displaySegmentos.slice(0, 8).map(s => s.segmento),
    datasets: [{
      data: displaySegmentos.slice(0, 8).map(s => s.totalTarifa),
      backgroundColor: CHART_COLORS,
      borderWidth: 2,
    }]
  };

  const doughnutOpts = {
    responsive: true,
    plugins: {
      legend: { position: 'right' },
      title: { display: true, text: 'Tarifa por Segmento', font: { size: 14 } }
    }
  };

  const topStats = statistics?.topSegmentos || [];

  const renderPriorityBadge = (p) => {
    const colors = { ALTA: 'danger', MEDIA: 'warning', BAJA: 'info' };
    return <Badge bg={colors[p] || 'secondary'}>{p}</Badge>;
  };

  const renderStatCard = (title, value, icon, color = 'primary', sub = null) => (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex align-items-center">
          <div className={`text-${color} me-3`}>
            <FontAwesomeIcon icon={icon} size="2x" />
          </div>
          <div>
            <div className="fs-4 fw-bold">{value}</div>
            <div className="text-muted">{title}</div>
            {sub && <small className="text-muted">{sub}</small>}
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  if (loading && !statistics) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="infomedia-dashboard">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Dashboard Infomedia</h2>
          <p className="text-muted mb-0">Competencia y mapeo por período</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-warning" onClick={handleDetect} disabled={loading || loadingOpt}>
            <FontAwesomeIcon icon={faSearch} className="me-1" />
            {loading ? 'Detectando...' : 'Detectar Sin Mapear'}
          </Button>
          <Button variant="outline-primary" href="/infomedia/competencia" className="me-1">
            Panorama competencia
          </Button>
          <Button variant="primary" href="/infomedia/mapping">
            <FontAwesomeIcon icon={faCog} className="me-1" /> Ir a Mapeo
          </Button>
        </div>
      </div>

      {/* Selector período */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end g-3">
            <Col md={3}>
              <Form.Label className="fw-semibold">Año</Form.Label>
              <Form.Select
                value={selectedPeriod.ano}
                onChange={e => setSelectedPeriod(p => ({ ...p, ano: parseInt(e.target.value) }))}
              >
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold">Mes</Form.Label>
              <Form.Select
                value={selectedPeriod.mes}
                onChange={e => setSelectedPeriod(p => ({ ...p, mes: parseInt(e.target.value) }))}
              >
                {MONTH_NAMES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Button variant="outline-primary" onClick={() => { loadDashboard(); loadOptimized(); }} disabled={loading || loadingOpt}>
                🔄 Actualizar
              </Button>
            </Col>
            <Col md={3} className="text-end">
              <span className="fs-5 fw-bold text-primary">
                {MONTH_NAMES[selectedPeriod.mes - 1]} {selectedPeriod.ano}
              </span>
              {(loading || loadingOpt) && <Spinner animation="border" size="sm" className="ms-2" />}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* KPIs */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          {renderStatCard('Mapeos Activos', statistics?.totalMapeos || 0, faCheckCircle, 'success')}
        </Col>
        <Col md={3}>
          {renderStatCard('Segmentos Únicos', statistics?.segmentosUnicos || 0, faChartPie, 'info')}
        </Col>
        <Col md={3}>
          {renderStatCard('Medios Cubiertos', statistics?.mediosUnicos || 0, faEye, 'primary')}
        </Col>
        <Col md={3}>
          {renderStatCard('Sin Mapear', unmappedSummary?.total || 0, faExclamationTriangle, 'warning', 'combinaciones')}
        </Col>
      </Row>

      {/* Tabs */}
      <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={k => setActiveTab(k)}>
        <Nav.Item><Nav.Link eventKey="competencia">📊 Competencia</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="mapeo">🗂️ Mapeo</Nav.Link></Nav.Item>
      </Nav>

      {/* ═══════════ TAB COMPETENCIA ═══════════ */}
      {activeTab === 'competencia' && (
        <>
          {/* Aviso datos sin mapear */}
          {sinSegmento.length > 0 && sinSegmento[0].totalSpots > 0 && (
            <Alert variant="info" className="mb-3">
              <strong>{sinSegmento[0].totalSpots?.toLocaleString()} spots</strong> sin segmento asignado aún.
              Completa el mapeo para verlos reflejados en las gráficas.
            </Alert>
          )}

          {/* Gráficas */}
          {displaySegmentos.length > 0 ? (
            <Row className="mb-4 g-3">
              <Col lg={7}>
                <Card className="h-100">
                  <Card.Body>
                    <Bar data={barData} options={barOpts} />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={5}>
                <Card className="h-100">
                  <Card.Body>
                    <Doughnut data={doughnutData} options={doughnutOpts} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          ) : (
            <Card className="mb-4 text-center py-5">
              <Card.Body>
                <FontAwesomeIcon icon={faChartBar} size="3x" className="text-muted mb-3" />
                <h5>No hay datos de competencia para este período</h5>
                <p className="text-muted">Verifica que haya datos cargados y mapeos activos.</p>
              </Card.Body>
            </Card>
          )}

          {/* Tabla detalle por segmento */}
          {displaySegmentos.length > 0 && (
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Detalle por Segmento — {MONTH_NAMES[selectedPeriod.mes - 1]} {selectedPeriod.ano}</h6>
                <Button variant="outline-success" size="sm">
                  <FontAwesomeIcon icon={faDownload} className="me-1" /> Exportar
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Segmento</th>
                      <th className="text-end">Spots</th>
                      <th className="text-end">Tarifa Total</th>
                      <th className="text-end">Tarifa Prom.</th>
                      <th className="text-end">Medios</th>
                      <th className="text-end">Versiones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displaySegmentos.map((s, i) => (
                      <tr key={i}>
                        <td>
                          <FontAwesomeIcon
                            icon={MEDIO_ICONS[s.segmento] || faChartBar}
                            className="me-2 text-muted"
                          />
                          <strong>{s.segmento}</strong>
                        </td>
                        <td className="text-end">{s.totalSpots?.toLocaleString()}</td>
                        <td className="text-end">${s.totalTarifa?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td className="text-end">${s.tarifaPromedio?.toFixed(2)}</td>
                        <td className="text-end">{s.mediosUnicos}</td>
                        <td className="text-end">{s.versionesUnicas}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light fw-bold">
                    <tr>
                      <td>Total</td>
                      <td className="text-end">{displaySegmentos.reduce((a, s) => a + (s.totalSpots || 0), 0)?.toLocaleString()}</td>
                      <td className="text-end">${displaySegmentos.reduce((a, s) => a + (s.totalTarifa || 0), 0)?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* Top segmentos de mapeos activos */}
          {topStats.length > 0 && (
            <Card>
              <Card.Header><h6 className="mb-0">Top Segmentos por Mapeos Activos</h6></Card.Header>
              <Card.Body>
                <Row>
                  {topStats.map((s, i) => (
                    <Col key={i} md={4} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <div>
                          <div className="fw-bold">{s.segmento}</div>
                          <small className="text-muted">mapeos activos</small>
                        </div>
                        <div className="fs-4 fw-bold text-primary">{s.cantidad}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {/* ═══════════ TAB MAPEO ═══════════ */}
      {activeTab === 'mapeo' && (
        <>
          {/* Estado de detección */}
          {unmappedStatus && (
            <Alert variant={unmappedStatus.yaDetectado ? 'info' : 'warning'} className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Detección:</strong> {unmappedStatus.mensaje}
                  {unmappedStatus.yaDetectado && (
                    <div className="mt-1">
                      <small>
                        🔍 Sin mapear: <strong>{unmappedStatus.combinacionesDetectadas}</strong> |
                        📊 Registros: <strong>{unmappedStatus.registrosAfectados?.toLocaleString()}</strong> |
                        💰 Tarifa: <strong>${unmappedStatus.tarifaTotal?.toLocaleString()}</strong>
                      </small>
                    </div>
                  )}
                </div>
                {!unmappedStatus.yaDetectado && (
                  <Button variant="warning" size="sm" onClick={handleDetect} disabled={loading}>
                    <FontAwesomeIcon icon={faSearch} className="me-1" /> Detectar
                  </Button>
                )}
              </div>
            </Alert>
          )}

          {/* Panel período actual */}
          {currentPeriodData && (
            <Card className="mb-4 border-success">
              <Card.Header className="bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 text-success">
                    <FontAwesomeIcon icon={faRocket} className="me-2" />
                    {MONTH_NAMES[selectedPeriod.mes - 1]} {selectedPeriod.ano}
                  </h6>
                  {loadingOpt && <Spinner animation="border" size="sm" />}
                </div>
              </Card.Header>
              <Card.Body>
                <Row className="mb-3 g-3">
                  {[
                    { label: 'Total Combinaciones', val: currentPeriodData.estadisticas?.totalCombinaciones || 0, cls: 'bg-light' },
                    { label: 'Mapeadas', val: currentPeriodData.estadisticas?.combinacionesMapeadas || 0, cls: 'bg-success bg-opacity-10' },
                    { label: 'Sin Mapear', val: currentPeriodData.estadisticas?.combinacionesSinMapear || 0, cls: 'bg-warning bg-opacity-10' },
                  ].map((item, i) => (
                    <Col md={4} key={i}>
                      <div className={`text-center p-3 rounded ${item.cls}`}>
                        <div className="h4 mb-0">{item.val}</div>
                        <small className="text-muted">{item.label}</small>
                      </div>
                    </Col>
                  ))}
                </Row>

                {currentPeriodData.sinMapear?.items?.length > 0 && (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="text-warning mb-0">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                        Prioritarios Sin Mapear
                      </h6>
                      <Button variant="warning" size="sm"
                        href={`/infomedia/mapping?ano=${selectedPeriod.ano}&mes=${selectedPeriod.mes}`}>
                        Ver Todos →
                      </Button>
                    </div>
                    <Table hover size="sm" responsive>
                      <thead>
                        <tr>
                          <th>Prioridad</th><th>Medio</th><th>Versión</th>
                          <th>Campaña</th><th>Registros</th><th>Tarifa</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentPeriodData.sinMapear.items.slice(0, 5).map((item, i) => (
                          <tr key={i}>
                            <td>{renderPriorityBadge(item.prioridadAtencion)}</td>
                            <td><small>{item.medioOriginal}</small></td>
                            <td><div style={{ maxWidth: 150 }} className="text-truncate" title={item.versionOriginal}><small>{item.versionOriginal}</small></div></td>
                            <td><div style={{ maxWidth: 130 }} className="text-truncate"><small>{item.campanaOriginal}</small></div></td>
                            <td><strong>{item.cantidadRegistros?.toLocaleString()}</strong></td>
                            <td><strong>${item.totalTarifa?.toLocaleString()}</strong></td>
                            <td>
                              <Button variant="outline-primary" size="sm"
                                href={`/infomedia/mapping?ano=${selectedPeriod.ano}&mes=${selectedPeriod.mes}&search=${encodeURIComponent(item.versionOriginal)}`}>
                                <FontAwesomeIcon icon={faPlus} /> Mapear
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Casos críticos sin mapear */}
          {unmappedSummary?.items?.length > 0 && (
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Casos Críticos Sin Mapear</h6>
                <Button variant="outline-primary" size="sm" href="/infomedia/unmapped">Ver Todos</Button>
              </Card.Header>
              <Card.Body>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Prioridad</th><th>Medio</th><th>Versión Original</th>
                      <th>Campaña</th><th>Registros</th><th>Tarifa Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmappedSummary.items.slice(0, 10).map((item, i) => (
                      <tr key={i}>
                        <td>{renderPriorityBadge(item.prioridadAtencion)}</td>
                        <td>{item.medioOriginal}</td>
                        <td><div className="text-truncate" style={{ maxWidth: 200 }} title={item.versionOriginal}>{item.versionOriginal}</div></td>
                        <td>{item.campanaOriginal}</td>
                        <td>{item.cantidadRegistros?.toLocaleString()}</td>
                        <td>${item.totalTarifa?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* Estado vacío mapeo */}
          {!loading && (!statistics || statistics.totalMapeos === 0) && (
            <Card className="text-center py-5">
              <Card.Body>
                <FontAwesomeIcon icon={faChartBar} size="3x" className="text-muted mb-3" />
                <h5>No hay mapeos para este período</h5>
                <p className="text-muted">Ejecuta la detección para comenzar a mapear.</p>
                <Button variant="primary" onClick={handleDetect}>
                  <FontAwesomeIcon icon={faSearch} className="me-1" /> Detectar Sin Mapear
                </Button>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
